<script setup lang="ts">
import type { SiteSectionCategoryId, SiteSectionId, V4HSiteId } from "@/data/common/ids";
import type { DateRange } from "@/domains/v4b/site-consumption/site-consumption.types";

import { isEqual, remove } from "es-toolkit";
import { computed, ref, watch } from "vue";

import { useDashboardMonoController } from "@/app/data/dashboard/dashboard-mono.controller";
import { EDashboardType } from "@/app/data/dashboard/dashboard.types";
import { ActiveFilter } from "@/components/section/site-section-active-filters.types";
import { ITabElement } from "@/components/ui/tabs/tabs.types";
import { useDashboardDateRange } from "@/composables/dashboard/use-dashboard-date-range";
import { useDashboardMonositeQueries } from "@/composables/dashboard/use-dashboard-monosite-queries";
import { useFeatureFlag } from "@/composables/feature-flag/use-feature-flag";
import { useManageSiteSectionCategories } from "@/composables/kili/sections/use-manage-site-section-categories";
import { useGetSiteDetailsQuery } from "@/composables/kili/sites/queries/use-get-site-details-query";
import { useV4hSiteSectionsForestQuery } from "@/composables/kili/sites/queries/use-get-site-sections-forest-query";
import { useBrandedNumberRouteParams } from "@/composables/use-route-params/use-hospitality-number-route-param";
import { DATA_TEST_IDS } from "@/constants/data-test-ids/data-test-ids.constant";
import { useTypedIntl } from "@/locales/typed-intl";
import router from "@/router/router";
import { ERouteName } from "@/router/router.type";
import { newSectionManagementFeatureFlag } from "@/services/feature-flags/env-feature-flags";
import { EMixpanelEvent } from "@/services/mixpanel/mixpanel-events.type";
import { MixpanelService } from "@/services/mixpanel/mixpanel-service";
import $date, { EDateScaling } from "@/utils/date";

import Insights from "@/app/data/consumption-monitoring/cards/insights.vue";
import AreaConsumptionChartMono from "@/app/data/dashboard/cards/mono/charts/area-consumption-chart-mono.vue";
import ConsumptionComparisonChartMono from "@/app/data/dashboard/cards/mono/charts/consumption-comparison-chart-mono.vue";
import ConsumptionEvolutionChartMono from "@/app/data/dashboard/cards/mono/charts/consumption-evolution-chart-mono.vue";
import YearlyConsumptionHeatmapMono from "@/app/data/dashboard/cards/mono/charts/yearly-consumption-heatmap-mono.vue";
import MissingDsoSubscriptionDialog from "@/app/data/dashboard/dialog/missing-dso-subscription-dialog.vue";
import MissingSectionSurfaceAreaMonoDialog from "@/app/data/dashboard/dialog/missing-section-surface-area-mono-dialog.vue";
import SiteConsumptionMonitoringSectionCategoryFilter from "@/components/data/dashboard/filter/site-consumption-monitoring-section-category-filter.vue";
import SelectSiteSectionPerimeter from "@/components/data/dashboard/select-perimeter/select-site-section-perimeter.vue";
import { TCustomBreadcrumbRouteFormatter } from "@/components/layout/base-layout-breadcrumb.vue";
import BaseLayout from "@/components/layout/base-layout.vue";
import SiteSectionCategoryActiveFilters from "@/components/section/site-section-active-filters.vue";
import ColoredBadge from "@/components/ui/colored-badge.vue";
import DateRangePicker from "@/components/ui/date-range-picker/date-range-picker.vue";
import Tabs from "@/components/ui/tabs/tabs.vue";

const currentRouteName = computed(() => router.currentRoute.value.name);

const { brandedNumberRouteParam: siteId } = useBrandedNumberRouteParams<V4HSiteId>("siteId");
const { t, k } = useTypedIntl();
const { isEnvFeatureEnabled } = useFeatureFlag();

const { data: siteDetails, isLoading: isFetchingSiteDetails } = useGetSiteDetailsQuery(siteId);
const { data: siteSectionsForest, isLoading: isFetchingSiteSectionsForest } = useV4hSiteSectionsForestQuery(siteId);

const { TO_DAY, FROM_DAY } = useDashboardDateRange();

const minPickableDate = computed(() => $date.subtract(TO_DAY, 1, EDateScaling.YEAR));

const selectedStartDate = ref<Date>(FROM_DAY);
const selectedEndDate = ref<Date>(TO_DAY);

const dateRange = computed<DateRange>(() => {
  return {
    from: $date.formatLocalDate(selectedStartDate.value),
    to: $date.formatLocalDate(selectedEndDate.value),
  };
});

const selectedSectionsMap = ref<Map<SiteSectionId, string>>(new Map());
const requestedSectionIds = computed<Array<SiteSectionId>>(() => {
  if (selectedSectionsMap.value.size === 0) return [];

  return Array.from(selectedSectionsMap.value.keys()) satisfies Array<SiteSectionId>;
});

const requestedSectionCategoryIds = ref<SiteSectionCategoryId[]>([]);

const { sectionCategoryFilterOptions, isCreatingSectionCategory, createSectionCategory } =
  useManageSiteSectionCategories(siteId, requestedSectionCategoryIds);

const { siteConsumption, missingInfos, isSiteConsumptionPending } = useDashboardMonositeQueries(
  siteId,
  dateRange,
  requestedSectionIds,
  requestedSectionCategoryIds,
);

const {
  isMissingDsoSubscriptionDialogVisible,
  isMissingSectionSurfaceAreaDialogVisible,
  isMissingAllSectionSurfaceArea,
} = useDashboardMonoController(siteConsumption, missingInfos, undefined);

const breadcrumbFormatter = computed<TCustomBreadcrumbRouteFormatter>(() => {
  return {
    [ERouteName.CONSUMPTION_MONITORING_SITE_DETAILS]: () => {
      return siteDetails.value?.name ?? t(k.data.consumptionMonitoring.siteMonitoring.title);
    },
  };
});

const activeCategoryFilters = computed<ActiveFilter<"section", SiteSectionCategoryId>[]>(() =>
  requestedSectionCategoryIds.value.flatMap((categoryId) => {
    const option = sectionCategoryFilterOptions.value.find((categoryOption) => categoryOption.id === categoryId);

    return option
      ? [
          {
            type: "section",
            id: categoryId,
            label: option.label,
          },
        ]
      : [];
  }),
);

function resetSectionCategoryFilter() {
  requestedSectionCategoryIds.value = [];
}

function removeSectionCategoryFromFilters(categoryId: SiteSectionCategoryId) {
  remove(requestedSectionCategoryIds.value, (c) => c === categoryId);

  MixpanelService.trackEvent(EMixpanelEvent.SECTION_CATEGORY_FILTER_CLEARED, {
    page_context: currentRouteName.value?.toString(),
    method: "single",
  });
}

enum ESiteConsumptionTabs {
  CONSUMPTION_CURVES = "curves",
  CONSUMPTION_COMPARISON = "comparison",
  SECTION_AREA_CONSUMPTION = "area",
  HEATMAP = "heatmap",
}

const tabElements: ITabElement<ESiteConsumptionTabs>[] = [
  {
    key: ESiteConsumptionTabs.CONSUMPTION_CURVES,
    label: "data.consumptionMonitoring.siteMonitoring.tabs.temporalConsumptionChart",
  },
  {
    key: ESiteConsumptionTabs.CONSUMPTION_COMPARISON,
    label: "data.consumptionMonitoring.siteMonitoring.tabs.consumptionComparisonChart",
  },
  {
    key: ESiteConsumptionTabs.SECTION_AREA_CONSUMPTION,
    label: "data.consumptionMonitoring.siteMonitoring.tabs.areaConsumptionChart",
  },
  {
    key: ESiteConsumptionTabs.HEATMAP,
    label: "data.consumptionMonitoring.siteMonitoring.tabs.calendarHeatmapChart",
  },
];

const tabMixpanelEventMap: Record<ESiteConsumptionTabs, EMixpanelEvent> = {
  [ESiteConsumptionTabs.CONSUMPTION_CURVES]: EMixpanelEvent.CONSUMPTION_MONITORING_GLOBAL_TAB,
  [ESiteConsumptionTabs.CONSUMPTION_COMPARISON]: EMixpanelEvent.CONSUMPTION_MONITORING_EVOLUTION_TAB,
  [ESiteConsumptionTabs.SECTION_AREA_CONSUMPTION]: EMixpanelEvent.CONSUMPTION_MONITORING_PER_M2,
  [ESiteConsumptionTabs.HEATMAP]: EMixpanelEvent.CONSUMPTION_MONITORING_HEATMAP,
};

function handleSelectTab(tab: ITabElement<ESiteConsumptionTabs>) {
  const tabKey = tab.key;

  MixpanelService.trackEvent(tabMixpanelEventMap[tabKey]);
}

function handleUpdateSectionSelection(nextSelection: Map<SiteSectionId, string>) {
  const isSameSelection = isEqual(nextSelection, selectedSectionsMap.value);
  if (isSameSelection) return;

  selectedSectionsMap.value = new Map(nextSelection);
  resetSectionCategoryFilter();

  MixpanelService.trackEvent(EMixpanelEvent.CONSUMPTION_MONITORING_UPDATE_SECTION_PERIMETER, {
    space_id: [...selectedSectionsMap.value.keys()],
  });
}

function getSelectSiteButtonText(): string | undefined {
  if (selectedSectionsMap.value.size === 0) {
    return siteDetails.value?.name;
  } else if (selectedSectionsMap.value.size === 1) {
    const maybeSection = selectedSectionsMap.value.entries().next();
    return maybeSection.value?.[1];
  }
}

function handleSubmitSectionCategoryIdsSelection(nextSectionCategoryIds: SiteSectionCategoryId[]) {
  const isSameSelection = isEqual(nextSectionCategoryIds, requestedSectionCategoryIds.value);
  if (isSameSelection) return;

  requestedSectionCategoryIds.value = [...nextSectionCategoryIds];

  if (requestedSectionCategoryIds.value.length) {
    MixpanelService.trackEvent(EMixpanelEvent.SECTION_CATEGORY_FILTER_APPLIED, {
      category_id: requestedSectionCategoryIds.value,
      page_context: currentRouteName.value?.toString(),
    });
  } else {
    MixpanelService.trackEvent(EMixpanelEvent.SECTION_CATEGORY_FILTER_CLEARED, {
      page_context: currentRouteName.value?.toString(),
      method: "dropdown",
    });
  }
}

watch(dateRange, () => {
  MixpanelService.trackEvent(EMixpanelEvent.CONSUMPTION_MONITORING_DATE_RANGE_CHANGED, {
    period_range: `${dateRange.value.from} - ${dateRange.value.to}`,
  });
});
</script>

<template>
  <BaseLayout
    :breadcrumb-formatter="breadcrumbFormatter"
    class="consumption-monitoring"
    :data-test-id="DATA_TEST_IDS.views.siteConsumptionMonitoring.view"
  >
    <div class="flex flex-col gap-6 py-8">
      <div class="flex w-full justify-between">
        <RouterLink
          class="flex gap-2 items-center font-semibold"
          :data-test-id="DATA_TEST_IDS.views.siteConsumptionMonitoring.backLink"
          :to="{ name: ERouteName.CONSUMPTION_MONITORING }"
        >
          <i class="pi pi-arrow-left text-xs" />
          {{ t(k.data.consumptionMonitoring.siteMonitoring.backToSitesList) }}
        </RouterLink>

        <div class="flex gap-6">
          <SiteConsumptionMonitoringSectionCategoryFilter
            v-if="isEnvFeatureEnabled(newSectionManagementFeatureFlag)"
            :section-category-filter-options="sectionCategoryFilterOptions"
            :create-category="createSectionCategory"
            :is-creating-category="isCreatingSectionCategory"
            :initial-selection="requestedSectionCategoryIds"
            @submit-selection="handleSubmitSectionCategoryIdsSelection($event)"
          />

          <DateRangePicker
            v-model:end-date="selectedEndDate"
            v-model:start-date="selectedStartDate"
            :max-date="TO_DAY"
            :min-date="minPickableDate"
          />
        </div>
      </div>
      <Insights
        :data-test-id="DATA_TEST_IDS.views.siteConsumptionMonitoring.insights"
        :is-section-scoped="!!selectedSectionsMap.size"
        :is-site-consumption-pending="isSiteConsumptionPending"
        :missing-infos="missingInfos"
        :site-consumption="siteConsumption"
        :site-id="siteId"
        @open-missing-subscription-dialog="isMissingDsoSubscriptionDialogVisible = true"
      >
        <template #header>
          <div
            class="flex flex-col gap-4"
            :data-test-id="DATA_TEST_IDS.views.siteConsumptionMonitoring.insightsHeader"
          >
            <div class="flex gap-3 items-center">
              <ColoredBadge :name="siteDetails?.name" />

              <SelectSiteSectionPerimeter
                :initial-selected-sections="selectedSectionsMap"
                :site-name="siteDetails?.name"
                :site-sections-forest="siteSectionsForest"
                :is-loading="isFetchingSiteDetails || isFetchingSiteSectionsForest"
                @update-selection="handleUpdateSectionSelection"
              >
                <template #buttonText>
                  <template v-if="getSelectSiteButtonText()">{{ getSelectSiteButtonText() }}</template>
                  <template v-else>{{
                    t(k.data.consumptionMonitoring.siteMonitoring.sectionPerimeter.multipleSectionSelection)
                  }}</template>
                </template>
              </SelectSiteSectionPerimeter>

              <SiteSectionCategoryActiveFilters
                :active-category-filters="activeCategoryFilters"
                @remove-section-category="(filter) => removeSectionCategoryFromFilters(filter.id)"
              />
            </div>
          </div>
        </template>
      </Insights>

      <Tabs
        :elements="tabElements"
        preload
        @item-clicked="handleSelectTab($event)"
      >
        <template #curves>
          <ConsumptionEvolutionChartMono
            :from-day="selectedStartDate"
            :site-id="siteId"
            :to-day="selectedEndDate"
            :selected-section-ids="requestedSectionIds"
            :selected-section-category-ids="requestedSectionCategoryIds"
          />
        </template>
        <template #comparison>
          <ConsumptionComparisonChartMono
            :from-day="selectedStartDate"
            :site-id="siteId"
            :to-day="selectedEndDate"
            :selected-section-ids="requestedSectionIds"
            :selected-section-category-ids="requestedSectionCategoryIds"
          />
        </template>
        <template #area>
          <AreaConsumptionChartMono
            :from-day="selectedStartDate"
            :site-id="siteId"
            :to-day="selectedEndDate"
            :selected-section-ids="requestedSectionIds"
            :selected-section-category-ids="requestedSectionCategoryIds"
          />
        </template>
        <template #heatmap>
          <YearlyConsumptionHeatmapMono :site-id="siteId" />
        </template>
      </Tabs>
    </div>
  </BaseLayout>

  <MissingSectionSurfaceAreaMonoDialog
    v-model:visible="isMissingSectionSurfaceAreaDialogVisible"
    :no-data="isMissingAllSectionSurfaceArea"
    :site-id="siteId"
  />

  <MissingDsoSubscriptionDialog
    v-model:visible="isMissingDsoSubscriptionDialogVisible"
    :type="EDashboardType.MONOSITE"
  />
</template>

<style lang="scss">
.consumption-monitoring {
  .site-sections-tree-section-category-filter {
    height: 42px;
    width: 215px;
  }
}
</style>
